/**
 * Permission keys registry.
 *
 * Every permission follows the `resource:action` pattern.
 * Permissions apply to all drug schedules uniformly.
 */

export const PERMISSIONS = {
  // User Management
  "users:view": "users:view",
  "users:create": "users:create",
  "users:deactivate": "users:deactivate",

  // Inventory
  "inventory:view": "inventory:view",
  "inventory:add_product": "inventory:add_product",
  "inventory:edit": "inventory:edit",
  "inventory:delete_product": "inventory:delete_product",
  // Dispenses
  "dispenses:view": "dispenses:view",
  "dispenses:create": "dispenses:create",
  "dispenses:edit": "dispenses:edit",
  "dispenses:delete": "dispenses:delete",

  // Receipts
  "receipts:view": "receipts:view",
  "receipts:create": "receipts:create",
  "receipts:edit": "receipts:edit",
  "receipts:delete": "receipts:delete",

  // Stock Adjustments
  "adjustments:create": "adjustments:create",

  // Inventory Audits
  "audits:create": "audits:create",

  // Reports
  "reports:view": "reports:view",
  "reports:delete": "reports:delete",

  // Audit Log
  "audit_log:view": "audit_log:view",

  // Settings
  "settings:general": "settings:general",
  "settings:pharmacy": "settings:pharmacy",
  "settings:subscription": "settings:subscription",

  // Reminders
  "reminders:view": "reminders:view",
  "reminders:create": "reminders:create",
  "reminders:edit": "reminders:edit",
  "reminders:delete": "reminders:delete",
  "reminders:complete": "reminders:complete",

  // Analytics
  "analytics:view": "analytics:view",

  // Alerts
  "alerts:view": "alerts:view",

  // Activity
  "activity:view": "activity:view",

  // Patients
  "patients:view": "patients:view",
  "patients:create": "patients:create",
  "patients:edit": "patients:edit",
  "patients:delete": "patients:delete",
} as const;

export type Permission = keyof typeof PERMISSIONS;

/**
 * Permission categories for UI grouping.
 */
export interface PermissionCategory {
  id: string;
  label: string;
  permissions: Permission[];
}

export const PERMISSION_CATEGORIES: PermissionCategory[] = [
  {
    id: "users",
    label: "User Management",
    permissions: ["users:view", "users:create", "users:deactivate"],
  },
  {
    id: "inventory",
    label: "Inventory",
    permissions: [
      "inventory:view",
      "inventory:add_product",
      "inventory:edit",
      "inventory:delete_product",
    ],
  },
  {
    id: "dispenses",
    label: "Dispensing",
    permissions: [
      "dispenses:view",
      "dispenses:create",
      "dispenses:edit",
      "dispenses:delete",
    ],
  },
  {
    id: "receipts",
    label: "Receipts",
    permissions: [
      "receipts:view",
      "receipts:create",
      "receipts:edit",
      "receipts:delete",
    ],
  },
  {
    id: "adjustments",
    label: "Stock Adjustments",
    permissions: ["adjustments:create"],
  },
  {
    id: "audits",
    label: "Inventory Audits",
    permissions: ["audits:create"],
  },
  {
    id: "reports",
    label: "Reports",
    permissions: ["reports:view", "reports:delete"],
  },
  {
    id: "audit_log",
    label: "Audit Log",
    permissions: ["audit_log:view"],
  },
  {
    id: "settings",
    label: "Settings",
    permissions: [
      "settings:general",
      "settings:pharmacy",
      "settings:subscription",
    ],
  },
  {
    id: "reminders",
    label: "Reminders",
    permissions: [
      "reminders:view",
      "reminders:create",
      "reminders:edit",
      "reminders:delete",
      "reminders:complete",
    ],
  },
  {
    id: "patients",
    label: "Patients",
    permissions: [
      "patients:view",
      "patients:create",
      "patients:edit",
      "patients:delete",
    ],
  },
  {
    id: "analytics",
    label: "Analytics & Monitoring",
    permissions: ["analytics:view", "alerts:view", "activity:view"],
  },
];

/**
 * Human-readable labels for each permission.
 */
export const PERMISSION_LABELS: Record<Permission, string> = {
  "users:view": "View Users",
  "users:create": "Create Users",
  "users:deactivate": "Deactivate Users",
  "inventory:view": "View Inventory",
  "inventory:add_product": "Add Products",
  "inventory:edit": "Edit Products",
  "inventory:delete_product": "Delete Products",
  "dispenses:view": "View Dispenses",
  "dispenses:create": "Create Dispenses",
  "dispenses:edit": "Edit Dispenses",
  "dispenses:delete": "Delete Dispenses",
  "receipts:view": "View Receipts",
  "receipts:create": "Create Receipts",
  "receipts:edit": "Edit Receipts",
  "receipts:delete": "Delete Receipts",
  "adjustments:create": "Create Adjustments",
  "audits:create": "Create Audits",
  "reports:view": "View Reports",
  "reports:delete": "Delete Reports",
  "audit_log:view": "View Audit Log",
  "settings:general": "General Settings",
  "settings:pharmacy": "Pharmacy Settings",
  "settings:subscription": "Subscription Settings",
  "reminders:view": "View Reminders",
  "reminders:create": "Create Reminders",
  "reminders:edit": "Edit Reminders",
  "reminders:delete": "Delete Reminders",
  "reminders:complete": "Complete Reminders",
  "analytics:view": "View Analytics",
  "alerts:view": "View Alerts",
  "activity:view": "View Activity",
  "patients:view": "View Patients",
  "patients:create": "Create Patients",
  "patients:edit": "Edit Patients",
  "patients:delete": "Delete Patients",
};
